// Conciliación de Emitidos. Financiero → Comprobantes Electrónicos.
//
// Mira en el sentido contrario a la Bandeja de Emitidos: no "qué mandé y cómo me
// fue", sino **qué tiene el SRI autorizado a mi nombre que mi sistema no
// registra** — y que por lo tanto no se declara ni en el 104 ni en el ATS.
//
// El caso que la motivó: la secuencia saltaba de la 2031 a la 2034 porque el
// worker consumía un secuencial, algo fallaba antes de guardar la clave, y al
// reintentar tomaba el siguiente número. Las 2032 y 2033 quedaron autorizadas
// en el SRI y huérfanas aquí.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, CheckCircle2, Clock, DownloadCloud, ExternalLink, GitCompare, Loader2,
  ScanLine, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useTabsStore } from '@/stores/tabs.store';
import { ExportarBotones, useMarcaEmpresa } from '@/lib/utils/exportarTabla';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const usd = (v: number) => (Number(v) || 0).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const fecha = (d: string | null) => (d ? new Date(d + 'T00:00:00').toLocaleDateString(LOCALE) : '—');
const urlDoc = (r: string | null | undefined) => (r ? r.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null);

interface Huerfano {
  documento_id: string; fecha_emision: string; tipo_comprobante: string; serie_comprobante: string;
  razon_social: string | null; ruc: string | null; importe_total: number; clave_acceso: string;
  ride_url: string | null; anulado_por: string | null; gemelos: string | null;
  veredicto: 'vivo_sin_declarar' | 'anulado' | 'nc_sin_registrar';
}
interface Hueco { serie: string; tipo: string }
interface NoConfirmado {
  ticket_id: string; fecha_emision: string; tipo_comprobante: string; serie_comprobante: string;
  razon_social: string | null; importe_total: number; clave_acceso: string | null;
}
interface SinXml {
  documento_id: string; clave_acceso: string; serie_comprobante: string;
  tipo_comprobante: string; fecha_emision: string; importe_total: number;
  detalle_fuente: string; dias_restantes: number;
}
interface Conciliacion {
  desde: string; hasta: string;
  cobertura: { ultimo_descargado: string | null; descargados_periodo: number; emitidos_sistema: number };
  resumen: { huerfanos: number; huerfanos_vivos: number; huerfanos_monto_vivo: number; huecos: number; no_confirmados: number };
  huerfanos: Huerfano[]; huecos: Hueco[]; no_confirmados: NoConfirmado[];
}

const VEREDICTO: Record<Huerfano['veredicto'], { label: string; cls: string }> = {
  vivo_sin_declarar: { label: 'Vivo · sin declarar', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  nc_sin_registrar:  { label: 'NC sin registrar',    cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  anulado:           { label: 'Anulado',             cls: 'bg-muted text-muted-foreground' },
};

const hoy = new Date();
const iso = (d: Date) => d.toISOString().slice(0, 10);
const primeroDelMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);

export function ConciliacionEmitidos() {
  const [desde, setDesde] = useState(iso(primeroDelMes));
  const [hasta, setHasta] = useState(iso(hoy));
  const openTab = useTabsStore((s) => s.openTab);
  const marca = useMarcaEmpresa();
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();

  // Sin el XML no se puede decidir nada de un huérfano: ni qué anula una nota de
  // crédito ni de qué paciente es una consulta. Y el web service del SRI sólo lo
  // sirve DIEZ DÍAS (medido el 21/09/2026: hasta 10 responde, desde el 11 no),
  // así que aquí se ve cuánto queda de esa ventana tan corta.
  const qx = useQuery<SinXml[]>({
    queryKey: ['fin-emitidos-sin-xml'],
    queryFn: async () => (await apiClient.post('/rpc/sri_emitidos_sin_xml', { _dias: 10 }, FIN)).data,
  });
  const sinXml = qx.data ?? [];
  const diasMin = sinXml.length ? Math.min(...sinXml.map((x) => Number(x.dias_restantes))) : null;

  // Una NC descargada sólo del listado no declara qué anula, y sin eso el guard
  // de clasificación (script 244) la bloquea con razón. Aquí se aporta ese dato
  // a mano, consultándolo en el portal del SRI, y queda constancia de quién lo
  // puso — una afirmación humana no vale lo mismo que un XML firmado.
  const fijarRef = useMutation({
    mutationFn: async (v: { id: string; num: string }) =>
      (await apiClient.post('/rpc/sri_emitido_fijar_referencia',
        { _documento_id: v.id, _num_doc_modificado: v.num }, FIN)).data as { message: string },
    onSuccess: (r) => {
      toast.success(r.message, { duration: 9000 });
      void qc.invalidateQueries({ queryKey: ['fin-conciliacion-emitidos'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pedirReferencia = async (h: Huerfano) => {
    const v = await dlg.pedir(`¿Qué comprobante anula la ${h.serie_comprobante}?`, [
      { key: 'num', label: 'Número del comprobante (formato 001-101-000000000)', valor: '001-101-000000' },
    ], { detalle: `Consúltalo en el portal del SRI con su clave de acceso:\n${h.clave_acceso}` });
    if (v && v.num.trim()) fijarRef.mutate({ id: h.documento_id, num: v.num.trim() });
  };

  const completar = useMutation({
    mutationFn: async (claves: string[] | null) =>
      (await apiClient.post('/rpc/sri_crear_job_completar_xml',
        { _claves: claves, _dias: 10 }, FIN)).data as { total: number; message: string },
    onSuccess: (r) => {
      toast.success(r.message);
      void qc.invalidateQueries({ queryKey: ['fin-emitidos-sin-xml'] });
      openTab({ id: '/sistemas/procesos', title: 'Monitor de procesos', path: '/sistemas/procesos' });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const q = useQuery<Conciliacion>({
    queryKey: ['fin-conciliacion-emitidos', desde, hasta],
    queryFn: async () =>
      (await apiClient.post('/rpc/fin_emitidos_conciliacion', { _desde: desde, _hasta: hasta }, FIN)).data,
  });
  const d = q.data;

  const campo = 'rounded-md border bg-background px-2.5 py-1.5 text-sm';

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <GitCompare className="w-5 h-5 text-primary" /> Conciliación de Emitidos
        </h1>
      </div>
      <p className="text-sm text-muted-foreground max-w-4xl">
        Lo que el SRI tiene autorizado a nombre de la empresa y el sistema no registra. Un comprobante
        huérfano existe ante el SRI pero no se declara: ni en el Formulario 104 ni en el ATS.
      </p>

      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4">
        <label className="flex flex-col text-xs text-muted-foreground">Desde
          <input type="date" className={`${campo} mt-1`} value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
        <label className="flex flex-col text-xs text-muted-foreground">Hasta
          <input type="date" className={`${campo} mt-1`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
        {q.isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary mb-2" />}
      </CardContent></Card>

      {d && <>
        {/* Veredicto */}
        <Card className={d.resumen.huerfanos > 0 ? 'border-red-500/60' : ''}>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
            {d.resumen.huerfanos > 0
              ? <><ShieldAlert className="w-4 h-4 text-red-600" /> {d.resumen.huerfanos} comprobante(s) del SRI sin registrar</>
              : <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Todo lo que el SRI tiene está registrado</>}
          </CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            {d.resumen.huerfanos > 0 && (
              <p>
                Suman <strong className="text-red-600">{usd(d.resumen.huerfanos_monto_vivo)}</strong> que
                no se están declarando (ya descontadas las notas de crédito huérfanas).
              </p>
            )}
            {/* Sin esto, «no confirmado» no significa nada: puede ser que el mes no esté descargado. */}
            <p className="text-muted-foreground">
              Descarga del SRI: {d.cobertura.descargados_periodo} comprobante(s) en el período; el último
              descargado es del <strong>{fecha(d.cobertura.ultimo_descargado)}</strong>. El sistema emitió{' '}
              {d.cobertura.emitidos_sistema} en el mismo lapso.
            </p>
          </CardContent>
        </Card>

        {/* Sin XML — hay que traerlo antes de que el SRI lo suelte */}
        {sinXml.length > 0 && (
          <Card className={diasMin !== null && diasMin <= 15 ? 'border-amber-500/60' : ''}>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600" /> {sinXml.length} comprobante(s) sin XML
            </CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p>
                Se bajaron sólo del listado del SRI. Sin el XML no se puede saber qué factura anula una
                nota de crédito ni a qué paciente corresponde una consulta — y eso es lo que decide si un
                huérfano se registra o se anula. Pasados los 10 días sólo queda el RIDE en PDF.
              </p>
              <p className="text-muted-foreground">
                El web service del SRI sólo sirve el XML de los <strong>últimos 10 días</strong>.{' '}
                {diasMin !== null && (diasMin <= 0
                  ? <strong className="text-red-600">Al más antiguo ya se le cerró la ventana.</strong>
                  : <>Al más antiguo le quedan <strong className={diasMin <= 15 ? 'text-red-600' : ''}>{diasMin} día(s)</strong>.</>)}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  disabled={completar.isPending}
                  onClick={() => completar.mutate(null)}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50">
                  {completar.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />}
                  Completar los {sinXml.length} por web service
                </button>
                {d.huerfanos.length > 0 && (
                  <button
                    disabled={completar.isPending}
                    onClick={() => completar.mutate(d.huerfanos.map((h) => h.clave_acceso))}
                    className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50">
                    Sólo los {d.huerfanos.length} huérfanos de este período
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                No usa el portal ni el navegador: pide cada XML al web service oficial por su clave de
                acceso, así que el reCAPTCHA del portal no lo afecta. El avance se ve en el Monitor de procesos.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Huérfanos — el bloque que importa */}
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Huérfanos — están en el SRI, no en el sistema ({d.huerfanos.length})</CardTitle>
            {d.huerfanos.length > 0 && (
              <ExportarBotones
                nombre={`conciliacion-emitidos-${desde}-${hasta}`}
                titulo="Comprobantes del SRI sin registrar"
                marca={marca}
                headers={['Fecha', 'Tipo', 'Comprobante', 'Receptor', 'Importe', 'Situación', 'Anulado por', 'Probable reemplazo']}
                colsDerecha={[4]}
                filas={d.huerfanos.map((h) => [
                  fecha(h.fecha_emision), h.tipo_comprobante, h.serie_comprobante,
                  h.razon_social || '—', h.importe_total, VEREDICTO[h.veredicto].label,
                  h.anulado_por || '—', h.gemelos || '—',
                ])}
              />
            )}
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Comprobante</th>
                <th className="py-2 pr-3">Receptor</th><th className="py-2 pr-3 text-right">Importe</th>
                <th className="py-2 pr-3">Situación</th><th className="py-2 pr-3">Probable reemplazo</th>
                <th className="py-2 pr-3"></th>
              </tr></thead>
              <tbody>
                {d.huerfanos.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">
                    Nada huérfano en el período.
                  </td></tr>
                )}
                {d.huerfanos.map((h) => (
                  <tr key={h.documento_id} className="border-b last:border-0 align-top">
                    <td className="py-1.5 pr-3 whitespace-nowrap">{fecha(h.fecha_emision)}</td>
                    <td className="py-1.5 pr-3">
                      <div className="font-mono">{h.serie_comprobante}</div>
                      <div className="text-xs text-muted-foreground">{h.tipo_comprobante}</div>
                    </td>
                    <td className="py-1.5 pr-3">
                      {h.razon_social || <span className="text-muted-foreground">—</span>}
                      {h.ruc && <div className="text-xs text-muted-foreground font-mono">{h.ruc}</div>}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{usd(h.importe_total)}</td>
                    <td className="py-1.5 pr-3">
                      <span className={`inline-block rounded px-2 py-0.5 text-xs ${VEREDICTO[h.veredicto].cls}`}>
                        {VEREDICTO[h.veredicto].label}
                      </span>
                      {h.anulado_por && <div className="text-xs text-muted-foreground mt-1 font-mono">{h.anulado_por}</div>}
                    </td>
                    <td className="py-1.5 pr-3 text-xs text-muted-foreground font-mono max-w-[16rem] break-words">
                      {h.gemelos || '—'}
                    </td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted"
                          title="Registrarlo en el sistema para que se declare"
                          onClick={() => openTab({ id: '/sri/procesar-emitidos', title: 'Clasificar Emitidos', path: '/sri/procesar-emitidos' })}>
                          <ScanLine className="w-3.5 h-3.5" /> Registrar
                        </button>
                        {h.veredicto === 'nc_sin_registrar' && !h.anulado_por && (
                          <button
                            className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted"
                            title="Esta nota de crédito no declara qué comprobante anula; indícalo consultando el portal del SRI"
                            onClick={() => pedirReferencia(h)}>
                            Indicar qué anula
                          </button>
                        )}
                        {h.veredicto === 'vivo_sin_declarar' && (
                          <button
                            className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted"
                            title="Emitir una nota de crédito que lo anule ante el SRI"
                            onClick={() => openTab({ id: '/cxc/notas-credito-emitir', title: 'Notas de Crédito', path: '/cxc/notas-credito-emitir' })}>
                            Anular
                          </button>
                        )}
                        {urlDoc(h.ride_url) && (
                          <a className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted"
                             href={urlDoc(h.ride_url)!} target="_blank" rel="noreferrer" title="Ver el RIDE">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {d.huerfanos.length > 0 && (
              <p className="text-xs text-muted-foreground mt-3 max-w-4xl">
                <strong>Registrar</strong> lo incorpora al sistema para que se declare — es lo correcto cuando la
                venta existió. <strong>Anular</strong> emite una nota de crédito: es lo correcto cuando el
                comprobante se duplicó y el cliente ya tiene otro por el mismo concepto (mira la columna
                «probable reemplazo»).
              </p>
            )}
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-4">
          {/* Huecos de numeración */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Huecos de numeración ({d.huecos.length})</CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-2">
                Números que la secuencia consumió y que no existen ni aquí ni en el SRI: se quemaron en el
                vacío. No hay daño fiscal — explican el salto de numeración.
              </p>
              {d.huecos.length === 0
                ? <div className="flex items-center gap-2 text-sm text-emerald-600"><CheckCircle2 className="w-4 h-4" /> La numeración es continua.</div>
                : <ul className="text-sm space-y-1">
                    {d.huecos.map((k) => (
                      <li key={k.serie} className="flex items-center gap-2">
                        <span className="font-mono">{k.serie}</span>
                        <span className="text-xs text-muted-foreground">{k.tipo}</span>
                      </li>
                    ))}
                  </ul>}
            </CardContent>
          </Card>

          {/* No confirmados */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Sin confirmar en el SRI ({d.no_confirmados.length})</CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-2">
                El sistema los da por autorizados y la descarga del portal no los respalda. <strong>Si el
                período no está descargado por completo, esto es normal</strong> — el último comprobante
                descargado es del {fecha(d.cobertura.ultimo_descargado)}.
              </p>
              {d.no_confirmados.length === 0
                ? <div className="flex items-center gap-2 text-sm text-emerald-600"><CheckCircle2 className="w-4 h-4" /> Todo lo emitido está respaldado.</div>
                : <div className="overflow-x-auto max-h-80">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="py-1.5 pr-3">Fecha</th><th className="py-1.5 pr-3">Comprobante</th>
                        <th className="py-1.5 pr-3 text-right">Importe</th>
                      </tr></thead>
                      <tbody>
                        {d.no_confirmados.map((n) => (
                          <tr key={n.ticket_id} className="border-b last:border-0">
                            <td className="py-1 pr-3 whitespace-nowrap">{fecha(n.fecha_emision)}</td>
                            <td className="py-1 pr-3 font-mono">{n.serie_comprobante}</td>
                            <td className="py-1 pr-3 text-right font-mono tabular-nums">{usd(n.importe_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>}
            </CardContent>
          </Card>
        </div>

        {d.resumen.huerfanos > 0 && (
          <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Mientras queden huérfanos vivos, el ATS y el Formulario 104 del período declaran menos de lo
              que el SRI tiene registrado a tu nombre.
            </span>
          </div>
        )}
      </>}
    </div>
  );
}
